import { gql } from '@apollo/client';

/**
 * The list manager (plan 4.6 #2): lists (STATIC, people added by hand or from a
 * filter) and saved filters (DYNAMIC), largest first. The CRM holds about 1,850
 * of them, so the API pages them; each page is one root field.
 *
 * Backend: ilbp-backend docs/CRM_TAG_AND_LIST_MANAGER.md. Admin-only.
 */

const LIST_FIELDS = `
  id
  name
  description
  type
  source
  isActive
  contactCount
  countedAt
  nbCreatedAt
  nbSource
  createdAt
  updatedAt
`;

export const CRM_LIST_MANAGER = gql`
  query CrmListManager($search: String, $type: SegmentType, $skip: Int, $take: Int) {
    crmSegments(search: $search, type: $type, skip: $skip, take: $take) {
      total
      items {
        ${LIST_FIELDS}
      }
    }
  }
`;

/** How many of each kind, for the switch above the table. Asked once a visit, and again after a change. */
export const CRM_LIST_TOTALS = gql`
  query CrmListTotals {
    lists: crmSegments(type: STATIC, take: 1) {
      total
    }
    savedFilters: crmSegments(type: DYNAMIC, take: 1) {
      total
    }
  }
`;

export const CRM_MANAGER_CREATE_LIST = gql`
  mutation CrmManagerCreateList($name: String!, $description: String) {
    crmCreateList(name: $name, description: $description) {
      ${LIST_FIELDS}
    }
  }
`;

/** Only the fields sent change; "" clears the description. */
export const CRM_UPDATE_LIST = gql`
  mutation CrmUpdateList($id: Int!, $name: String, $description: String) {
    crmUpdateList(id: $id, name: $name, description: $description) {
      ${LIST_FIELDS}
    }
  }
`;

export const CRM_DELETE_LIST = gql`
  mutation CrmDeleteList($id: Int!) {
    crmDeleteList(id: $id)
  }
`;

/** NationBuilder's "save filter": the contact list's filter, run again whenever it is used. */
export const CRM_SAVE_FILTER = gql`
  mutation CrmSaveFilter($name: String!, $filter: CrmContactFilter!, $description: String) {
    crmSaveFilter(name: $name, filter: $filter, description: $description) {
      ${LIST_FIELDS}
    }
  }
`;

/**
 * NationBuilder's "save as list": the people the filter matches now, kept as a list.
 * `expectedCount` is the number the admin confirmed; on any other the API makes nothing.
 */
export const CRM_SAVE_FILTER_AS_LIST = gql`
  mutation CrmSaveFilterAsList($name: String!, $filter: CrmContactFilter!, $expectedCount: Int!, $description: String) {
    crmSaveFilterAsList(name: $name, filter: $filter, expectedCount: $expectedCount, description: $description) {
      matched
      changed
      list {
        ${LIST_FIELDS}
      }
    }
  }
`;

/* ------------------------------------------------------------------ *
 * A list's own page (card 20)
 * ------------------------------------------------------------------ */

/** One list or saved filter, with a saved filter's stored filter. */
export const CRM_LIST_DETAIL = gql`
  query CrmListDetail($id: Int!) {
    crmList(id: $id) {
      ${LIST_FIELDS}
      filter
    }
  }
`;

/**
 * Who is on it now: the consent counts and the first people, by the contact list's own
 * filter, `segments: { anyOf: [id] }`. For a saved filter that is whoever its filter matches.
 */
export const CRM_LIST_PEOPLE = gql`
  query CrmListPeople($filter: CrmContactFilter!) {
    counts: crmContactCounts(filter: $filter) {
      total
      withEmail
      withPhone
      canEmail
      canCall
      canText
      doNotCall
      doNotContact
    }
    people: crmContacts(filter: $filter, take: 10) {
      items {
        id
        fullName
        primaryEmail
        primaryPhone
        city
        countryCode
      }
    }
  }
`;

/** Counts a list's members, or a saved filter's matches, again, and stores the number. */
export const CRM_RECOUNT_LIST = gql`
  mutation CrmRecountList($id: Int!) {
    crmRecountList(id: $id) {
      ${LIST_FIELDS}
    }
  }
`;
